import { Injectable } from '@angular/core';
import { GridApi } from 'ag-grid-enterprise';
import { CookieService } from 'ngx-cookie-service';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { UserStateService } from '../../services/user-state.service';
import { StateCategory } from '../../store/models/user-state.model';
@Injectable({
  providedIn: 'root'
})
export class TableStateService {
  constructor(private cookieService: CookieService,
  private userStateService: UserStateService) { }

  public init(
    key: string,
    defaultPageSize: number,
    initialColumns: any[],
    columnsGenerator: any,
    storage: TableStateStorage = TableStateStorage.sessionStorage
  ): TableStateInstance {
    const instance = new TableStateInstance(key, defaultPageSize, initialColumns, columnsGenerator, storage, this.cookieService, this.userStateService);
    return instance;
  }
}

export class TableStateInstance {
  constructor(key: string,
    defaultPageSize: number,
    initialColumns: any[],
    columnsGenerator: any,
    storage: TableStateStorage = TableStateStorage.sessionStorage,
    private cookieService: CookieService,
    private userStateService: UserStateService)
  {
    this._stateKey = key;
    this.defaultPageSize = defaultPageSize;
    this._initialColumns = initialColumns;
    this.generateColumns = columnsGenerator;
    this._storageType = storage;
  }

  private _stateKey: string;
  private _storageType: TableStateStorage;
  private defaultPageSize: number;
  private _initialColumns: any[];
  private skip: number;
  private take: number;
  private _isWorking: boolean;
  private generateColumns: (columnNames: string[]) => any[];

  public set stateKey(key: string) {
    this._stateKey = key;
  }

  public set storageType(type: TableStateStorage) {
    this._storageType = type;
  }

  public set initialColumns(columns: any[]) {
    this._initialColumns = columns;
  }

  public get isWorking(): boolean {
    return this._isWorking;
  }

  private isSortChanged(gridApi: GridApi, state: TableState): boolean {
    const colState = gridApi?.getColumnState();

    const currentState = colState?.find(c => c.sort !== null);
    const newState = state?.columnState.state.find(c => c.sort !== null);

    return currentState?.colId !== newState?.colId || currentState?.sort !== newState?.sort;
  }

  private isGroupChanged(gridApi: GridApi, state: TableState): boolean {
    const colState = gridApi?.getColumnState();

    const currentState = colState?.find(c => c.rowGroup === true);
    const newState = state?.columnState.state.find(c => c.rowGroup === true);

    return currentState?.colId !== newState?.colId || currentState?.rowGroup !== newState?.rowGroup;
  }

  private isGroupOn(state: TableState): boolean {
    return state?.columnState.state.some(c => c.rowGroup === true);
  }

  getAutoGroupColIndex(colState) {
    return colState?.findIndex(column => column.colId === 'ag-Grid-AutoColumn');
  }

  applyTableState(gridApi: GridApi): Observable<TableStateResult> {
    let externalSort: boolean = false;
    let externalGroup: boolean = false;
    let hasGroups: boolean = false;

    if (!gridApi) {
      return of (null);
    }
    this._isWorking = true;

    return this.getTableState().pipe(map(state => {
      if (!state?.columnState && this._stateKey && this._storageType !== TableStateStorage.none) {
        state = this.initState(gridApi, true);
        this.setTableState(state);
      }

      if (!state) {
        this._isWorking = false;
        return null;
      }

      if (state?.columnState) {
        externalSort = this.isSortChanged(gridApi, state);
        externalGroup = this.isGroupChanged(gridApi, state);
        hasGroups = this.isGroupOn(state);

        const oldAutoColumnIndex = this.getAutoGroupColIndex(state.columnState.state);

        if (!!state.columnState.state) {
          const columnNames = state.columnState.state.filter(c => c.colId !== '_selectionChecks' && c.colId !== '_actions').map(c => c.colId);
          const columns = columnNames.length === 0 ? null : this.generateColumns(columnNames);
          if (!!columns) {
            gridApi?.setGridOption('columnDefs', []);
            gridApi?.setGridOption('columnDefs', columns);
          }
        } else {
          gridApi?.setGridOption('columnDefs', []);
          gridApi?.setGridOption('columnDefs', this._initialColumns);
        }

        gridApi?.applyColumnState({
          state: state.columnState.state,
          applyOrder: true,
          defaultState: { sort: null, rowGroup: null }
        });

        const newAutoColumnIndex = this.getAutoGroupColIndex(gridApi?.getColumnState());

        // need this - ag-grid bug
        if (oldAutoColumnIndex !== newAutoColumnIndex) {
          gridApi.moveColumnByIndex(newAutoColumnIndex, oldAutoColumnIndex);
        }

        if (!externalSort && !externalGroup) {
          this.applyPaging(gridApi).pipe(take(1)).subscribe();
        }
      }

      setTimeout(() => {
        this._isWorking = false;
      }, 1000);

      return {
        externalSort: externalSort,
        externalGroup: externalGroup,
        take: !!this.take ? this.take : 25,
        skip: !!this.skip ? this.skip : 0,
        hasGroups: hasGroups
      };
    })
    )
  }

  applyPaging(gridApi: GridApi): Observable<TableStateResult> {
    return this.getTableState().pipe(map(state => {
      const take = state?.pageState?.pageSize;
      const skip = state?.pageState?.pageNumber * take;
      gridApi?.setGridOption('paginationPageSize', take);
      gridApi?.paginationGoToPage(skip / take);
      this.take = take;
      this.skip = skip;
      return { take: !!this.take ? this.take : 25, skip: !!this.skip ? this.skip : 0 };
    })
    );
  }

  public getTableState(): Observable<TableState> {
    if (!this._stateKey || this._storageType === TableStateStorage.none) {
      return of(null);
    }
    try {
      if (this._storageType === TableStateStorage.db) {
        return this.userStateService.getUserState(StateCategory.Table, this._stateKey).pipe(map(state => {
            return state;
        })
        )
      }
      else {
        const json =
          this._storageType === TableStateStorage.localStorage
            ? localStorage.getItem(this._stateKey)
            : this._storageType === TableStateStorage.sessionStorage
              ? sessionStorage.getItem(this._stateKey)
              : this.cookieService.get(this._stateKey);
        if (json === null) {
          return of(null);
        }
        return of(JSON.parse(json));
      }
    } catch (err) {
      return of(null);
    }
  }

  public setTableState(state: TableState): void {
    if (!this._stateKey || this._storageType === TableStateStorage.none) {
      return;
    }
    if (state) {
      if (this._storageType === TableStateStorage.db) {
        this.userStateService.setUserState(StateCategory.Table, this._stateKey, JSON.stringify(state)).subscribe();
      }
      else {
        if (this._storageType === TableStateStorage.localStorage) {
          localStorage.setItem(this._stateKey, JSON.stringify(state));
        } else if (this._storageType === TableStateStorage.sessionStorage) {
          sessionStorage.setItem(this._stateKey, JSON.stringify(state));
        } else {
          this.cookieService.set(this._stateKey, JSON.stringify(state), { path: '/', secure: false, sameSite: 'Lax' });
        }
        return;
      }
    } else {
      if (this._storageType === TableStateStorage.db) {
        this.userStateService.clearUserState(StateCategory.Table, this._stateKey).subscribe();
      }
      else {
        if (this._storageType === TableStateStorage.localStorage) {
          localStorage.removeItem(this._stateKey);
        } else if (this._storageType === TableStateStorage.sessionStorage) {
          sessionStorage.removeItem(this._stateKey);
        } else {
          this.cookieService.delete(this._stateKey);
        }
      }
    }
  }

  saveColumnsState(gridApi: GridApi): void {
    if (!this._stateKey || this._storageType === TableStateStorage.none) {
      return;
    }
    this.getTableState().pipe(take(1)).subscribe(state => {

      if (!state?.columnState && this._stateKey) {
        state = this.initState(gridApi, false);
        this.setTableState(state);
      } else {
        state.columnState.state = gridApi?.getColumnState();
        if (state.columnState.state?.length === 0) {
          return;
        }
        state.columnState.state.map(c => {
          c.cellRenderer = undefined;
          c.cellRendererParams = undefined;
        });

        this.setTableState(state);
      }
    })
  }

  savePagingState(gridApi: GridApi): void {
    if (!this._stateKey || this._storageType === TableStateStorage.none) {
      return;
    }
    this.getTableState().pipe(take(1)).subscribe(state => {
      if (!state?.columnState && this._stateKey) {
        state = this.initState(gridApi, false);
        this.setTableState(state);
      } else {
        state.pageState = {
          pageSize: gridApi.paginationGetPageSize(),
          pageNumber: gridApi.paginationGetCurrentPage()
        };
        this.setTableState(state);
      }
    })

  }

  private initState(gridApi: GridApi, reset: boolean): TableState {
    if (!this._stateKey || this._storageType === TableStateStorage.none) {
      return null;
    }
    let state = {
      columnState: null,
      pageState: null
    } as TableState;

    if (gridApi) {
      const columns = this._initialColumns?.filter(c => c.colId !== '_selectionChecks' && c.colId !== '_actions');
      if (columns?.length) {
        const column = columns[0];

        let columnState = reset ? this._initialColumns : gridApi?.getColumnState();
        columnState.map(c => {
          c.colId === column.field ? (c.sort = 'asc') : (c.sort = null);
          c.rowGroup = false;
          c.rowGroupIndex = null;
          c.cellRenderer = undefined;
          c.cellRendererParams = undefined;
        });
        columnState = columnState.filter(c => !c.colId?.startsWith('ag-Grid'));
        state.columnState = {
          state: columnState,
          defaultState: { sort: null, rowGroup: null }
        };
      }
    }

    state.pageState = {
      pageSize: this.defaultPageSize,
      pageNumber: 0
    };

    return state;
  }

  resetState(gridApi: GridApi): TableStateResult {
    let externalSort: boolean = false;
    let externalGroup: boolean = false;
    let hasGroup: boolean = false;

    this.setTableState(null);

      const state = this.initState(gridApi, true);

      if (!state) {
        return null;
      }

      externalSort = this.isSortChanged(gridApi, state);
      externalGroup = this.isGroupChanged(gridApi, state);
      hasGroup = this.isGroupOn(state);
      gridApi?.applyColumnState(state.columnState);

      gridApi.setGridOption('paginationPageSize', state.pageState.pageSize);
      gridApi.paginationGoToPage(state.pageState.pageNumber);

      return { externalSort: externalSort, externalGroup: externalGroup, take: this.take, skip: this.skip, hasGroups: hasGroup };

  }

}

export enum TableStateStorage {
  none = 0,
  localStorage = 1,
  sessionStorage = 2,
  cookie = 3,
  db = 4
}

export interface TableStateResult {
  externalSort?: boolean;
  externalGroup?: boolean;
  take: number;
  skip: number;
  hasGroups?: boolean;
}

export interface TableState {
  columnState: any;
  pageState: any;
}
